import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { HelpHint } from "@/components/HelpHint";
import { formatDateTime } from "@/lib/helpers";
import { Megaphone, Send, Users, Eye } from "lucide-react";
import { toast } from "sonner";
import { TierBadge } from "@/components/presentation/TierBadge";
import { isDemoUser } from "@/lib/demoMode";
import { useAuth } from "@/context/AuthContext";

const AUDIENCES = [
  { value: "all_students", label: "All Students" },
  { value: "all_parents", label: "All Parents" },
  { value: "all_instructors", label: "All Instructors" },
  { value: "classroom", label: "A Specific Classroom" },
  { value: "location", label: "Students at a Specific Location" },
  { value: "day", label: "Students Scheduled on a Specific Day" },
  { value: "overdue_accounts", label: "Parents with an Overdue Balance" },
  { value: "missing_forms", label: "Parents with Missing Paperwork" },
];

const emptyForm = { title: "", message: "", audience_type: "all_parents", classroom_id: "", location: "", day: "" };

export default function CommunicationCenter() {
  const { user } = useAuth();
  const showTier = isDemoUser(user);
  const [form, setForm] = useState(emptyForm);
  const [classrooms, setClassrooms] = useState([]);
  const [locations, setLocations] = useState([]);
  const activeLocations = useMemo(() => locations.filter((l) => l.active), [locations]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [classroomsRes, locationsRes, historyRes] = await Promise.all([
        api.get("/classrooms"),
        api.get("/settings/meeting-locations"),
        api.get("/announcements"),
      ]);
      setClassrooms(classroomsRes.data);
      setLocations(locationsRes.data);
      setHistory(historyRes.data);
    } catch {
      toast.error("Could not load Communication Center");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const buildTargetPayload = () => ({
    audience_type: form.audience_type,
    classroom_id: form.audience_type === "classroom" ? form.classroom_id : undefined,
    location: form.audience_type === "location" ? form.location : undefined,
    day: form.audience_type === "day" ? form.day : undefined,
  });

  const runPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await api.post("/announcements/preview", buildTargetPayload());
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not preview recipients");
    } finally {
      setPreviewing(false);
    }
  };

  const send = async () => {
    if (!form.title || !form.message) {
      toast.error("Title and message are required");
      return;
    }
    setSending(true);
    try {
      const res = await api.post("/announcements", { ...buildTargetPayload(), title: form.title, message: form.message });
      toast.success(`Announcement sent to ${res.data.recipient_count} recipient${res.data.recipient_count === 1 ? "" : "s"}.`);
      setForm(emptyForm);
      setPreview(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send announcement");
    } finally {
      setSending(false);
    }
  };

  const audienceDescription = (a) => {
    const found = AUDIENCES.find((x) => x.value === a.audience_type);
    if (a.audience_type === "classroom") {
      const c = classrooms.find((cl) => cl.id === a.classroom_id);
      return `${found?.label}${c ? `: ${c.class_name}` : ""}`;
    }
    if (a.audience_type === "location") return `${found?.label}: ${a.location}`;
    if (a.audience_type === "day") return `${found?.label}: ${a.day}`;
    return found?.label || a.audience_type;
  };

  if (loading) return <Skeleton className="h-64 rounded-[var(--radius)]" />;

  return (
    <div className="space-y-6" data-testid="communication-center-page">
      <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-3" data-testid="announcement-compose-form">
        <div className="flex items-center gap-1.5">
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Megaphone className="h-5 w-5 text-[hsl(var(--role-accent))]" /> Compose Announcement</h2>
          <HelpHint text="Send one message to everyone who needs it instead of making individual phone calls." testId="communication-center-help" />
          {showTier && <TierBadge tier="professional" size="xs" />}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Weather Closure Today" data-testid="announcement-title-input" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Message</Label>
            <Textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder="Keep it short and clear" data-testid="announcement-message-textarea" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Audience</Label>
            <select
              className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm"
              value={form.audience_type}
              onChange={(e) => { setForm((f) => ({ ...f, audience_type: e.target.value })); setPreview(null); }}
              data-testid="announcement-audience-select"
            >
              {AUDIENCES.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>
          {form.audience_type === "classroom" && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Classroom</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.classroom_id} onChange={(e) => setForm((f) => ({ ...f, classroom_id: e.target.value }))} data-testid="announcement-classroom-select">
                <option value="">Select a classroom</option>
                {classrooms.map((c) => (
                  <option key={c.id} value={c.id}>{c.class_name}</option>
                ))}
              </select>
            </div>
          )}
          {form.audience_type === "location" && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Location</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} data-testid="announcement-location-select">
                <option value="">Select a location</option>
                {activeLocations.map((l) => (
                  <option key={l.id} value={l.name}>{l.name}</option>
                ))}
              </select>
            </div>
          )}
          {form.audience_type === "day" && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Day</Label>
              <Input type="date" value={form.day} onChange={(e) => setForm((f) => ({ ...f, day: e.target.value }))} data-testid="announcement-day-input" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 pt-1 flex-wrap">
          <Button variant="outline" onClick={runPreview} disabled={previewing} data-testid="announcement-preview-button">
            <Eye className="h-4 w-4 mr-2" /> {previewing ? "Checking..." : "Preview Recipients"}
          </Button>
          {preview && (
            <Badge variant="outline" className="gap-1.5" data-testid="announcement-recipient-count">
              <Users className="h-3.5 w-3.5" /> {preview.recipient_count} recipient{preview.recipient_count === 1 ? "" : "s"}
            </Badge>
          )}
          <Button onClick={send} disabled={sending} className="bg-[hsl(var(--role-accent))] hover:opacity-95 ml-auto" data-testid="announcement-send-button">
            <Send className="h-4 w-4 mr-2" /> {sending ? "Sending..." : "Send Announcement"}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="font-heading text-lg font-semibold">Announcement History</h2>
        {history.length === 0 ? (
          <div className="rounded-[var(--radius)] border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="announcement-history-empty">
            No announcements sent yet.
          </div>
        ) : (
          <div className="divide-y rounded-[var(--radius)] border bg-card" data-testid="announcement-history-list">
            {history.map((a) => (
              <div key={a.id} className="p-4 sm:p-5" data-testid="announcement-history-row">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-sm">{a.title}</p>
                  <Badge variant="outline" className="gap-1 shrink-0"><Users className="h-3 w-3" /> {a.recipient_count}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{a.message}</p>
                <p className="text-xs text-muted-foreground mt-2">{audienceDescription(a)} &middot; Sent by {a.sent_by} &middot; {formatDateTime(a.sent_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
